import React, { useState, useEffect, useMemo } from 'react';
import { useFeatureToggle } from '../../contexts/FeatureToggleContext';
import toast from 'react-hot-toast';

const FeatureToggles = () => {
  const { 
    features, 
    enableFeature, 
    disableFeature, 
    fetchAllFeatures,
    isFeatureEnabled
  } = useFeatureToggle();

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [togglingFeatures, setTogglingFeatures] = useState(new Set());

  // Load features on mount
  useEffect(() => {
    const loadFeatures = async () => {
      setLoading(true);
      try {
        await fetchAllFeatures();
      } catch (error) {
        console.error('❌ FeatureToggles: Error loading features:', error);
      } finally {
        setLoading(false);
      }
    };
    loadFeatures();
  }, [fetchAllFeatures]);

  // Group features by category
  const featuresByCategory = useMemo(() => {
    const grouped = {};
    features.forEach(feature => {
      if (!grouped[feature.category]) {
        grouped[feature.category] = [];
      }
      grouped[feature.category].push(feature);
    });
    return grouped;
  }, [features]);

  // Filter features based on search and category
  const filteredFeatures = useMemo(() => {
    let filtered = features;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(f => f.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(f => 
        f.feature_name.toLowerCase().includes(term) ||
        f.feature_name_en?.toLowerCase().includes(term) ||
        f.feature_key.toLowerCase().includes(term) ||
        f.description?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [features, selectedCategory, searchTerm]);

  // Handle toggle
  const handleToggle = async (featureKey, currentState) => {
    setTogglingFeatures(prev => new Set(prev).add(featureKey));

    try {
      const result = currentState 
        ? await disableFeature(featureKey)
        : await enableFeature(featureKey);

      if (result.success) {
        toast.success(
          currentState 
            ? `✅ Feature disabled: ${featureKey}`
            : `✅ Feature enabled: ${featureKey}`
        );
      } else {
        toast.error(result.message || 'Failed to toggle feature');
        
        // Show dependency error if present
        if (result.missing && result.missing.length > 0) {
          const deps = result.missing.map(d => d.name || d.key).join(', ');
          toast.error(`Missing dependencies: ${deps}`, { duration: 5000 });
        }
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setTogglingFeatures(prev => {
        const next = new Set(prev);
        next.delete(featureKey);
        return next;
      });
    }
  };

  // Bulk enable/disable by category
  const handleBulkToggle = async (category, enable) => {
    const categoryFeatures = featuresByCategory[category] || [];
    const featuresToToggle = enable 
      ? categoryFeatures.filter(f => !f.is_enabled)
      : categoryFeatures.filter(f => f.is_enabled);

    if (featuresToToggle.length === 0) {
      toast.error('No features to toggle');
      return;
    }

    const confirmMessage = enable
      ? `Enable ${featuresToToggle.length} features in ${category}?`
      : `Disable ${featuresToToggle.length} features in ${category}?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    for (const feature of featuresToToggle) {
      await handleToggle(feature.feature_key, feature.is_enabled);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      b2b: '🏢',
      general: '⚙️',
      analytics: '📊',
      integrations: '🔌',
      accessibility: '♿'
    };
    return icons[category] || '📦';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      b2b: 'B2B Features',
      general: 'General Features',
      analytics: 'Analytics',
      integrations: 'Integrations',
      accessibility: 'Accessibility'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading feature toggles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Feature Toggles</h1>
              <p className="text-gray-600 mt-1">
                Enable or disable system features dynamically
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">
                Total: {features.length} features
              </div>
              <div className="text-sm text-green-600">
                Enabled: {features.filter(f => f.is_enabled).length}
              </div>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search features..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="b2b">🏢 B2B Features</option>
              <option value="general">⚙️ General Features</option>
              <option value="analytics">📊 Analytics</option>
              <option value="integrations">🔌 Integrations</option>
              <option value="accessibility">♿ Accessibility</option>
            </select>
          </div>
        </div>

        {/* Features List */}
        {selectedCategory === 'all' ? (
          // Grouped by category
          Object.keys(featuresByCategory).map(category => {
            const categoryFeatures = featuresByCategory[category].filter(f => 
              !searchTerm || filteredFeatures.includes(f)
            );

            if (categoryFeatures.length === 0) return null;

            return (
              <div key={category} className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <span>{getCategoryIcon(category)}</span>
                    {getCategoryLabel(category)}
                    <span className="text-sm font-normal text-gray-500">
                      ({categoryFeatures.length})
                    </span>
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleBulkToggle(category, true)}
                      className="text-sm px-3 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100"
                    >
                      Enable All
                    </button>
                    <button
                      onClick={() => handleBulkToggle(category, false)}
                      className="text-sm px-3 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100"
                    >
                      Disable All
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {categoryFeatures.map(feature => (
                    <FeatureToggleItem
                      key={feature.id}
                      feature={feature}
                      isToggling={togglingFeatures.has(feature.feature_key)}
                      onToggle={handleToggle}
                    />
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          // Single category or search results
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="space-y-3">
              {filteredFeatures.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No features found</p>
                </div>
              ) : (
                filteredFeatures.map(feature => (
                  <FeatureToggleItem
                    key={feature.id}
                    feature={feature}
                    isToggling={togglingFeatures.has(feature.feature_key)}
                    onToggle={handleToggle}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Feature Toggle Item Component
const FeatureToggleItem = ({ feature, isToggling, onToggle }) => {
  const { isFeatureEnabled } = useFeatureToggle();
  const enabled = isFeatureEnabled(feature.feature_key);

  return (
    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-1">
          <h3 className="font-semibold text-gray-900">
            {feature.feature_name_en || feature.feature_name}
          </h3>
          <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
            {feature.feature_key}
          </code>
        </div>
        
        {(feature.description_en || feature.description) && (
          <p className="text-sm text-gray-600 mb-2">
            {feature.description_en || feature.description}
          </p>
        )}

        {feature.dependencies && feature.dependencies.length > 0 && (
          <div className="text-xs text-orange-600">
            <span className="font-semibold">Dependencies:</span>{' '}
            {feature.dependencies.join(', ')}
          </div>
        )}

        {feature.requires_restart && (
          <div className="text-xs text-yellow-600 mt-1">
            ⚠️ Requires server restart
          </div>
        )}
      </div>

      <div className="flex items-center gap-4">
        <span className={`text-sm font-medium ${enabled ? 'text-green-600' : 'text-gray-400'}`}>
          {enabled ? 'Enabled' : 'Disabled'}
        </span>
        <button
          onClick={() => onToggle(feature.feature_key, enabled)}
          disabled={isToggling}
          className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
            enabled ? 'bg-blue-600' : 'bg-gray-300'
          } ${isToggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span
            className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-7' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default FeatureToggles;
